import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarkdownLite } from './Plan';
import { fetchAgreements } from '../data/legal';
import PageWorld from '../components/site/PageWorld';
import { PHYSICAL, SECURITY } from '../data/worlds';

// `world` wraps the page in its 3D world (data/worlds); the legal pages
// pass none and stay plain documents.
function Frame({ title, lede, children, world = null }) {
  return (
    <PageWorld world={world}>
      <div className="cs-store">
        <header className="cs-store-hero">
          <h1>{title}</h1>
          <p style={{ maxWidth: 680 }}>{lede}</p>
        </header>
        <section className="cs-store-section" style={{ maxWidth: 720 }}>
          {children}
        </section>
      </div>
    </PageWorld>
  );
}

export function Physical() {
  return (
    <Frame
      world={PHYSICAL}
      title="GridOS"
      lede="The control plane for warehouses, fleets, and operators. It is a separate product from Platform and App Kit."
    >
      <p>
        Safety systems stay in charge. Local controllers are not overridden
        from the network. GridOS holds the live state and the actions that
        policy allows. A person takes the exception when confidence drops.
      </p>
      <p>
        Platform writes into systems you already use. App Kit is the app
        people open. GridOS coordinates physical operations. Prices for that
        work live on its own site, not on{' '}
        <Link to="/pricing">nventr.io pricing</Link>.
      </p>
      <div className="cs-land-cta-row">
        <a
          className="cs-btn cs-btn-primary"
          href="https://gridos.nventr.ai/"
          target="_blank"
          rel="noreferrer"
        >
          Open GridOS
        </a>
        <Link to="/pricing" className="cs-btn cs-btn-outline">
          Platform and App Kit pricing
        </Link>
      </div>
    </Frame>
  );
}

function LegalDoc({ kind, fallbackTitle }) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    fetchAgreements()
      .then((list) => { if (alive) setDoc(list.find((d) => d.kind === kind) || null); })
      .catch((err) => { if (alive) { setDoc(null); setError(err.message); } });
    return () => { alive = false; };
  }, [kind]);
  return (
    <Frame title={doc?.title || fallbackTitle} lede="Nventr AI, LLC. The version you agree to is the one recorded with your acceptance.">
      {doc === undefined && <p className="cs-help">Loading…</p>}
      {doc === null && <p className="cs-help">{error || 'This document is not available right now.'}</p>}
      {doc && <MarkdownLite text={doc.body} />}
      <p className="cs-help" style={{ marginTop: 24 }}>
        <Link to="/terms">Terms</Link>
        {' · '}
        <Link to="/privacy">Privacy</Link>
        {' · '}
        <Link to="/eula">End user agreement</Link>
      </p>
    </Frame>
  );
}

export function Privacy() {
  return <LegalDoc kind="privacy" fallbackTitle="Privacy" />;
}

export function Terms() {
  return <LegalDoc kind="tos" fallbackTitle="Terms" />;
}

export function Eula() {
  return <LegalDoc kind="eula" fallbackTitle="End user agreement" />;
}

export function Security() {
  return (
    <Frame
      world={SECURITY}
      title="Security"
      lede="How a write is allowed to happen."
    >
      <p>
        The model proposes. A gate decides. The gate is a person, or a
        keyword filter. An LLM yes or no is not a gate. Only the runtime
        commits, and the commit record names that runtime.
      </p>
      <p>
        A side effect has to be a capability you declared. Undeclared means
        the call does not happen. Credentials for those systems stay with
        the runtime. They are not placed in the model context.
      </p>
      <p>
        SAML is on Platform Business and on Kit Business. Usage meters store
        counts, not content. This page does not claim a certification.
      </p>
      <p>
        Report a problem on the <Link to="/contact">contact form</Link>.
      </p>
    </Frame>
  );
}
